import { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { authService } from '@services/auth.service';
import { deliveryService } from '@services/delivery.service';
import {
  ArrowUpFromLine,
  ClipboardCheck,
  LayoutDashboard,
  Users,
  Building2,
  FolderTree,
  Shield,
  KeyRound,
  FileCheck2,
  ShoppingCart,
  Wallet,
  Bike,
  Percent,
  Settings,
  ClipboardList,
  Menu,
  X,
  Bell,
  Search,
  LogOut,
  ChevronDown,
  ChevronRight,
  Tag,
  Package,
  Award,
  Truck,
  Scale,
  Activity,
  Route,
  Banknote,
  PieChart,
  Smartphone,
  AlertTriangle,
  SearchCheck,
  UserX,
} from 'lucide-react';
import { useAuthStore } from '@store/authStore';
import { cn } from '@lib/utils';
import { MapsProvider } from '@contexts/MapsContext';

interface NavItem {
  label: string;
  path: string;
  icon: React.ElementType;
  permission?: string;
  badge?: number;
  children?: NavItem[];
}

const navigationItems: NavItem[] = [
  { label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
  { label: 'Users', path: '/users', icon: Users, permission: 'users.read' },
  { label: 'Deletion Requests', path: '/deletion-requests', icon: UserX, permission: 'users.read' },
  { label: 'App Versions', path: '/app-versions', icon: Smartphone, permission: 'users.read' },
  { label: 'Notifications', path: '/notifications', icon: Bell, permission: 'users.read' },
  { label: 'Businesses', path: '/businesses', icon: Building2, permission: 'businesses.read' },
  { label: 'Departments', path: '/departments', icon: FolderTree, permission: 'departments.read' },
  { label: 'Roles', path: '/roles', icon: Shield, permission: 'roles.read' },
  { label: 'Permissions', path: '/permissions', icon: KeyRound, permission: 'permissions.read' },
  { label: 'Categories', path: '/categories', icon: Tag, permission: 'categories.read' },
  { label: 'Brands', path: '/brands', icon: Award, permission: 'brands.read' },
  { label: 'Products', path: '/products', icon: Package, permission: 'products.read' },
  { label: 'Applications', path: '/applications', icon: ClipboardCheck, permission: 'businesses.read' },
  { label: 'KYC Review', path: '/kyc', icon: FileCheck2, permission: 'kyc.read' },
  { label: 'Orders', path: '/orders', icon: ShoppingCart, permission: 'orders.read' },
  // Dispute management (backend requires chekit-level orders.update)
  { label: 'Disputes', path: '/disputes', icon: Scale, permission: 'orders.update' },
  {
    label: 'Finance',
    path: '/finance',
    icon: Wallet,
    permission: 'wallets.read',
    children: [
      { label: 'Money Dashboard', path: '/finance/dashboard', icon: PieChart, permission: 'wallets.read' },
      { label: 'Wallets', path: '/finance/wallets', icon: Wallet, permission: 'wallets.read' },
      { label: 'Ledger Health', path: '/finance/ledger', icon: Activity, permission: 'wallets.read' },
      { label: 'Money Trace', path: '/finance/trace', icon: Route, permission: 'wallets.read' },
      { label: 'Treasury', path: '/finance/treasury', icon: Banknote, permission: 'wallets.read' },
      { label: 'Withdrawals', path: '/finance/withdrawals', icon: ArrowUpFromLine, permission: 'wallets.read' },
      { label: 'Payment Claims', path: '/finance/payment-claims', icon: SearchCheck, permission: 'payments.read' },
      { label: 'Payments', path: '/finance/payments', icon: Wallet, permission: 'payments.read' },
      { label: 'Commission Requests', path: '/commissions/requests', icon: Percent, permission: 'commissions.read' },
      { label: 'Tax Reports', path: '/finance/tax', icon: Wallet, permission: 'reports.tax' },
    ],
  },
  { label: 'Riders', path: '/riders', icon: Bike, permission: 'riders.read' },
  {
    label: 'Delivery',
    path: '/delivery',
    icon: Truck,
    permission: 'settings.read',
    children: [
      { label: 'Overview',  path: '/delivery',           icon: Truck, permission: 'settings.read' },
      { label: 'Incidents', path: '/delivery/incidents', icon: AlertTriangle, permission: 'settings.read' },
      { label: 'Providers', path: '/delivery/providers', icon: Truck, permission: 'settings.read' },
      { label: 'Settings',  path: '/delivery/settings',  icon: Settings, permission: 'settings.read' },
    ],
  },
  { label: 'Commissions', path: '/commissions', icon: Percent, permission: 'commissions.read' },
  { label: 'Settings', path: '/settings', icon: Settings, permission: 'settings.read' },
  { label: 'Audit Logs', path: '/audit', icon: ClipboardList, permission: 'audit.read' },
];

export const DashboardLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, hasCheKitPermission, setUser } = useAuthStore();

  // Refresh user context from /me on mount.
  // If the access token expired the interceptor refreshes it automatically.
  // If refresh fails too, the interceptor redirects to /login.
  useEffect(() => {
    authService.fetchUserContext()
      .then(setUser)
      .catch(() => {
        // Silently ignore; the interceptor already handles redirect on auth failure.
      });
  }, []);

  // Open delivery incidents badge: disputed trips waiting on a support decision.
  // Read-only and best-effort, so a user without delivery access simply sees no badge.
  const { data: incidentCount } = useQuery({
    queryKey: ['delivery', 'incidents', 'count'],
    queryFn: () => deliveryService.getDeliveryIncidentCount(),
    refetchInterval: 60000,
    retry: false,
  });
  const openIncidents = incidentCount?.open ?? 0;

  const toggleExpanded = (path: string) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Filter navigation to only show items the user has permissions for
  const visibleItems = navigationItems.filter(
    (item) => !item.permission || hasCheKitPermission(item.permission)
  );

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  // Derive page title from current route
  const currentItem = navigationItems.find(
    (item) =>
      isActive(item.path) ||
      item.children?.some((child) => isActive(child.path))
  );
  const pageTitle = currentItem?.label || 'Dashboard';

  return (
    <MapsProvider>
    <div className="min-h-screen bg-[var(--bg-base)]">
      {/* Sidebar */}
      {/* Sidebar. Surface white against the mint page, so the nav reads as a
          panel rather than dissolving into the ground, with a forest-tinted
          hairline rather than a grey one. */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-[280px] bg-[var(--bg-surface)] border-r border-[var(--border-subtle)] transform transition-transform duration-200 ease-in-out lg:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex flex-col h-full p-6">
          {/* Logo. The wordmark was type-only; the other three portals all lead
              with the mark, so it is here too. Display face is Bricolage. */}
          <div className="flex items-center gap-2.5 px-3 mb-8">
            <img
              src="/chekit-logo.png"
              alt=""
              aria-hidden="true"
              className="h-9 w-9 object-contain shrink-0 block dark:hidden"
            />
            <img
              src="/chekit-logo-dark.png"
              alt=""
              aria-hidden="true"
              className="h-9 w-9 object-contain shrink-0 hidden dark:block"
            />
            <div>
              <span className="font-display font-bold text-[18px] tracking-[-0.02em] text-[var(--text-primary)]">
                Chekit
              </span>
              <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)]">
                Internal
              </div>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="ml-auto lg:hidden text-[var(--text-muted)] hover:text-[var(--text-primary)]"
            >
              <X className="w-5 h-5" strokeWidth={1.5} />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto space-y-0.5">
            {visibleItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              const hasChildren = item.children && item.children.length > 0;
              const isExpanded = expandedItems.has(item.path);

              if (hasChildren) {
                const visibleChildren = item.children!.filter(
                  (child) => !child.permission || hasCheKitPermission(child.permission)
                );
                if (visibleChildren.length === 0) return null;

                return (
                  <div key={item.path}>
                    <button
                      onClick={() => toggleExpanded(item.path)}
                      className={cn(
                        'w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[14px] font-medium transition-all duration-200',
                        active
                          ? 'font-semibold text-[var(--text-primary)] bg-[var(--bg-elevated)] border-l-2 border-[var(--gradient-to)]'
                          : 'text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-base)]'
                      )}
                    >
                      <Icon className="w-[18px] h-[18px] shrink-0" strokeWidth={1.5} />
                      <span className="flex-1 text-left">{item.label}</span>
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4" strokeWidth={1.5} />
                      ) : (
                        <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
                      )}
                    </button>
                    {isExpanded && (
                      <div className="ml-4 mt-0.5 space-y-0.5">
                        {visibleChildren.map((child) => (
                          <Link
                            key={child.path}
                            to={child.path}
                            onClick={() => setSidebarOpen(false)}
                            className={cn(
                              'flex items-center gap-3 px-3 py-2 rounded-xl text-[13px] font-medium transition-all duration-200',
                              isActive(child.path)
                                ? 'font-semibold text-[var(--text-primary)] bg-[var(--bg-elevated)]'
                                : 'text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-base)]'
                            )}
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-current" />
                            <span className="flex-1">{child.label}</span>
                            {child.path === '/delivery/incidents' && openIncidents > 0 && (
                              <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-[var(--state-warning-bg)] text-[var(--state-warning)]">
                                {openIncidents}
                              </span>
                            )}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2.5 rounded-xl text-[14px] font-medium transition-all duration-200',
                    active
                      ? 'font-semibold text-[var(--text-primary)] bg-[var(--bg-elevated)] border-l-2 border-[var(--gradient-to)]'
                      : 'text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-base)]'
                  )}
                >
                  <Icon className="w-[18px] h-[18px] shrink-0" strokeWidth={1.5} />
                  <span className="flex-1">{item.label}</span>
                  {item.badge != null && item.badge > 0 && (
                    <span className={cn(
                      'ml-auto text-[11px] font-semibold px-2 py-0.5 rounded-full',
                      active ? 'bg-[var(--bg-elevated)] text-[var(--text-accent)]' : 'bg-[var(--bg-base)] text-[var(--text-muted)]'
                    )}>
                      {item.badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* User card at bottom.
              This block was still dressed for a DARK sidebar that no longer
              exists: border-white/10, text-white and text-gray-400 on a white
              panel meant the name was invisible and the phone number nearly so.
              The avatar was `from-primary-500 to-secondary-500`, which before
              the compat layer was the banned indigo-to-purple gradient. */}
          <div className="pt-4 mt-4 border-t border-[var(--border-subtle)]">
            <div className="flex items-center gap-3 px-3">
              <div className="w-9 h-9 gradient-bg rounded-lg flex items-center justify-center text-white font-semibold text-sm shrink-0">
                {user?.firstName?.[0] || 'A'}{user?.lastName?.[0] || ''}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-[var(--text-primary)] truncate">
                  {user?.firstName} {user?.lastName}
                </div>
                <div className="text-xs text-[var(--text-muted)] truncate">{user?.phone}</div>
              </div>
              <button
                onClick={handleLogout}
                className="text-[var(--text-muted)] hover:text-[var(--state-danger)] transition-colors duration-200 shrink-0"
                title="Logout"
              >
                <LogOut className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 lg:hidden backdrop-blur-sm"
          style={{ background: 'rgba(15, 61, 30, 0.45)' }}
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="lg:pl-[280px]">
        {/* Header.
            Was `bg-white` with a hard `border-b`: a white slab wedged between
            the mint sidebar and the mint page, with a drawn line cutting the
            page in two. It is now glass (§9) over the mint ground, so the page
            reads as one continuous surface. saturate(1.6) is load-bearing: it
            pulls the green out of whatever scrolls underneath, where blur alone
            reads grey. The rule below fades out at both edges rather than
            running the full width. */}
        <header
          className="sticky top-0 z-30 px-8 py-5"
          style={{
            background:
              'linear-gradient(180deg, rgba(232,245,233,0.55) 0%, rgba(244,251,245,0.42) 100%)',
            backdropFilter: 'blur(20px) saturate(1.6)',
            WebkitBackdropFilter: 'blur(20px) saturate(1.6)',
          }}
        >
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 bottom-0 h-px"
            style={{
              background:
                'linear-gradient(90deg, transparent 0%, rgba(15,61,30,0.10) 30%, rgba(15,61,30,0.10) 70%, transparent 100%)',
            }}
          />
          <div className="flex items-center justify-between gap-6">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-[var(--text-muted)] hover:text-[var(--text-primary)]"
            >
              <Menu className="w-6 h-6" strokeWidth={1.5} />
            </button>

            <div className="flex-1">
              <h1 className="font-display text-[28px] font-medium tracking-[-0.02em] text-[var(--text-primary)] leading-tight">
                {pageTitle}
              </h1>
              <p className="text-[13px] text-[var(--text-muted)]">
                Welcome back, {user?.firstName || 'Admin'}!
              </p>
            </div>

            {/* Right Section */}
            <div className="flex items-center gap-3">
              {/* Search */}
              <div className="relative">
                <Search
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-muted)]"
                  strokeWidth={1.5}
                />
                <input
                  type="text"
                  placeholder="Search..."
                  className="w-[260px] pl-10 pr-4 py-2 rounded-full border border-[var(--border-subtle)] bg-[var(--bg-base)] text-[14px] placeholder:text-[var(--text-muted)] focus:border-[var(--border-default)] transition-colors"
                />
              </div>

              {/* Notifications */}
              <button className="relative w-9 h-9 text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-base)] rounded-full flex items-center justify-center transition-colors">
                <Bell className="w-[18px] h-[18px]" strokeWidth={1.5} />
                <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-[var(--state-danger)] rounded-full" />
              </button>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-8">
          <Outlet />
        </main>
      </div>
    </div>
    </MapsProvider>
  );
};
